import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Stethoscope, HeartPulse, Pill, NotebookPen, TriangleAlert,
  Plus, Trash2, Pencil, Activity, Save, Droplets, ClipboardList, FileText, HandHeart,
  Printer, Copy, ShieldAlert, FileSignature, History, BookmarkPlus, FolderOpen, ListChecks, RotateCcw, Download,
} from "lucide-react";
import { useApp, formatDate } from "../context/AppContext";
import { gerarReceitaSimples, gerarReceita2Vias, gerarReceitaEspecial } from "../utils/receituarioPdf";
import { gerarPrescricaoMensal } from "../utils/prescricaoMensalPdf";
import { RichTextEditor } from "../components/RichTextEditor";
import { gerarProntuarioDiaPDF, gerarAnotacoesPeriodoPDF } from "../utils/prontuarioPdf";
import { IMG_DECUBITO } from "../utils/decubito";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const F = ({ label, children }) => (<div><label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>{children}</div>);

const fmtDT = (iso) => {
  if (!iso) return "-";
  const [d, t] = iso.split("T");
  return `${formatDate(d)}${t ? " " + t.slice(0, 5) : ""}`;
};
const agoraISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// ---- Constantes de Medicação ----
const VIAS_ADMIN = ["COMPONENTE", "INALAR", "INJETAVEL", "MASTIGAVEL", "NASAL", "OCULAR",
  "ORAL", "OTOLOGICA", "SONDA", "SUBCUTANEA", "SUBLINGUAL", "TOPICO"];
const UNIDADES = [
  { v: "UN", l: "UN" }, { v: "KG", l: "KG" }, { v: "CP", l: "CP" }, { v: "AMP", l: "AMP" },
  { v: "ML", l: "ML" }, { v: "GOTAS", l: "GOTAS" }, { v: "FR", l: "FR" }, { v: "PCTE", l: "PCTE" },
  { v: "ROLO", l: "ROLO" }, { v: "TUBO", l: "TUBO" }, { v: "CAPS", l: "CAPS" }, { v: "JATO", l: "JATO" },
  { v: "SACHE", l: "SACHE" }, { v: "COLHER", l: "COLHER" }, { v: "UI", l: "UI" }, { v: "CX", l: "CX" },
  { v: "G", l: "G (grama)" }, { v: "MT", l: "MT (metro)" },
];
const HORARIOS_OPCOES = [
  "00:00 / 08:00 / 16:00", "00 04 08 12 16 20", "00h", "01 07 13 19", "02 10 18", "06:00",
  "06 10 14 18 21", "06 10 14 19", "06/12/18/00", "06/12/18 h", "06 14 19", "06 18", "07:00 hrs",
  "07/10/13/16/19", "07 11 15 18", "07/11/15/19", "07/11/18", "07 12", "07/12/18", "07/13/19",
  "07 16 21", "07/18 h", "07 19", "08 12", "08/19 h", "08h", "09:00 h", "09/13/17 h", "09 21",
  "10h", "11:00 / 21:00", "11:00 h", "12:00 hrs", "12 18", "13h", "14:00", "16:00 h", "16 21",
  "17:00 h", "17 21", "18:00 hrs", "19h", "20:00 h", "21:00", "22:00", "Se necessário",
];
// apresentação legível para a observação ("Tomar 1 comprimido...")
const APRESENTACAO = {
  UN: "unidade", KG: "kg", CP: "comprimido", AMP: "ampola", ML: "ml", GOTAS: "gota", FR: "frasco",
  PCTE: "pacote", ROLO: "rolo", TUBO: "tubo", CAPS: "cápsula", JATO: "jato", SACHE: "sachê",
  COLHER: "colher", UI: "UI", CX: "caixa", G: "grama", MT: "metro",
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");
// conta quantas vezes ao dia = número de horários informados (ex.: "06 18 22" = 3x; "06:00" = 1x)
const contarVezes = (horarios) => {
  if (!horarios) return 0;
  if (/necess/i.test(horarios)) return -1; // se necessário / SOS
  // captura HH:MM como um único horário (evita contar os minutos) ou HH sozinho
  const nums = horarios.match(/\d{1,2}\s*:\s*\d{2}|\d{1,2}/g) || [];
  return nums.length;
};
const pluralizar = (base, n) => {
  if (n <= 1) return base;
  if (base.endsWith("r")) return base + "es";      // colher -> colheres
  return base + "s";
};
// monta a observação automática da medicação
const montarObsMed = ({ dose, unidade, via, horarios, usoContinuo, diasTratamento }) => {
  const partes = [];
  if (dose) partes.push(dose);
  if (unidade) partes.push(unidade);
  if (via) partes.push(cap(via));
  if (horarios) partes.push(horarios);
  const vezes = contarVezes(horarios);
  const apres = APRESENTACAO[unidade] || (unidade ? unidade.toLowerCase() : "");
  let instr = "";
  if (dose || apres) {
    const doseNum = parseFloat(String(dose).replace(",", ".")) || 1;
    const apresTxt = apres ? pluralizar(apres, doseNum) : "";
    instr = `Tomar ${dose || ""} ${apresTxt}`.replace(/\s+/g, " ").trim();
    if (vezes === -1) instr += " se necessário";
    else if (vezes >= 1) instr += ` ${vezes}x ao dia`;
  }
  let cauda = "";
  if (usoContinuo) cauda = " - uso contínuo";
  else if (diasTratamento) cauda = ` - por ${diasTratamento} dias`;
  const cabeca = partes.join(" - ");
  return [cabeca, instr].filter(Boolean).join(" - ") + cauda;
};


const Prontuario = () => {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { getResidente, getProntuario, addRegistroProntuario, deleteRegistroProntuario, updateRegistroProntuario, updateProntuarioInfo, podeReceituario, podeApagarAnotacao, podeEditarAnotacaoSempre, auth, empresa } = useApp();
  const { toast } = useToast();
  const res = getResidente(id);
  const podeClinico = podeReceituario; // enfermeiro(a) / administrador editam itens clínicos
  // Evolução: só profissionais de saúde inserem; categoria definida pelo perfil.
  const EVOL_CAT = { enfermeiro: "Enfermagem", medico: "Médica", fisioterapeuta: "Fisioterapia", nutricionista: "Nutrição" };
  const podeInserirEvolucao = ["enfermeiro", "medico", "nutricionista", "fisioterapeuta"].includes(auth?.perfil);
  const minhaCategoriaEvol = EVOL_CAT[auth?.perfil] || "";
  const podeVerEvolucao = podeInserirEvolucao || auth?.perfil === "administrador";

  const stamp = ([auth?.nome, auth?.funcao].filter(Boolean).join(" — ")) +
    ((auth?.coren && auth?.perfil !== "cuidador") ? ` · ${auth.coren}` : "");

  const [registros, setRegistros] = useState([]);
  const [info, setInfo] = useState({});
  const [autor, setAutor] = useState(stamp || "Equipe de Enfermagem");
  const hojeStr = new Date().toISOString().slice(0, 10);
  const [periodoOpen, setPeriodoOpen] = useState(false);
  const [periodo, setPeriodo] = useState({ inicio: hojeStr, fim: hojeStr });

  const ABAS_PERMITIDAS = ["timeline", "sinais", "anotacao", "balanco", "ocorrencia", "prescricao_cuidados", "decubito",
    ...(podeClinico ? ["medicacao", "receituario"] : []),
    ...(podeVerEvolucao ? ["evolucao"] : [])];
  const tabParam = sp.get("tab");
  const abaInicial = ABAS_PERMITIDAS.includes(tabParam) ? tabParam : "timeline";

  const carregar = async () => {
    const d = await getProntuario(id);
    setRegistros(d.registros);
    setInfo(d.info);
  };
  useEffect(() => { if (id) carregar(); /* eslint-disable-next-line */ }, [id]);

  const porTipo = useMemo(() => {
    const g = { sinais_vitais: [], medicacao: [], evolucao: [], ocorrencia: [],
      anotacao_enfermagem: [], prescricao_medica: [], prescricao_cuidados: [], balanco_hidrico: [] };
    registros.forEach((r) => { (g[r.tipo] || (g[r.tipo] = [])).push(r); });
    return g;
  }, [registros]);

  const add = async (tipo, dados) => {
    const { _data, ...rest } = dados || {};
    await addRegistroProntuario(id, { tipo, data: _data || agoraISO(), autor, dados: rest });
    toast({ title: "Registro adicionado", description: "Prontuário atualizado." });
    carregar();
  };
  const remover = async (regId) => { await deleteRegistroProntuario(id, regId); carregar(); };
  const atualizar = async (regId, dados) => { await updateRegistroProntuario(id, regId, { dados }); carregar(); };

  const salvarInfo = async () => {
    await updateProntuarioInfo(id, info);
    toast({ title: "Resumo clínico salvo" });
  };
  const setI = (k, v) => setInfo((s) => ({ ...s, [k]: v }));

  const emitirPdfDia = () => {
    gerarProntuarioDiaPDF({ empresa, residente: res, registros, data: hojeStr });
    toast({ title: "PDF do dia gerado" });
  };
  const emitirPdfPeriodo = () => {
    if (periodo.inicio > periodo.fim) { toast({ title: "Data inicial maior que a final" }); return; }
    gerarAnotacoesPeriodoPDF({ empresa, residente: res, registros, inicio: periodo.inicio, fim: periodo.fim });
    setPeriodoOpen(false);
    toast({ title: "Relatório de anotações gerado" });
  };

  if (!res) return <div className="text-slate-500">Residente não encontrado. <Link to="/residentes" className="text-blue-600">Voltar</Link></div>;

  return (
    <div className="space-y-6">
      <Link to={`/residentes/${id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="w-4 h-4" /> Voltar para a ficha
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 flex items-center gap-4">
        {res.foto ? <img src={res.foto} alt={res.nome} className="w-14 h-14 rounded-2xl object-cover" />
          : <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-bold">{res.nome.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>}
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Stethoscope className="w-5 h-5 text-blue-600" /> Prontuário — {res.nome}</h1>
          <p className="text-sm text-slate-400">Quarto {res.quarto} · Internação {formatDate(res.dataInternacao)}</p>
        </div>
        <div className="flex flex-col items-stretch gap-2 w-72">
          <F label="Registrando como (profissional)"><input className={inputCls} value={autor} onChange={(e) => setAutor(e.target.value)} /></F>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={emitirPdfDia} data-testid="pdf-dia-btn">
              <Printer className="w-4 h-4" /> PDF do dia
            </Button>
            {podeReceituario && (
              <Button variant="outline" className="flex-1 gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setPeriodoOpen(true)} data-testid="relatorio-periodo-btn">
                <FileText className="w-4 h-4" /> Por período
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modal relatório por período (anotações de enfermagem) */}
      <Dialog open={periodoOpen} onOpenChange={setPeriodoOpen}>
        <DialogContent aria-describedby={undefined} data-testid="periodo-modal">
          <DialogHeader><DialogTitle>Relatório de Anotações de Enfermagem</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Escolha o período. O PDF traz as anotações de enfermagem agrupadas por dia.</p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Data inicial"><input type="date" className={inputCls} value={periodo.inicio} onChange={(e) => setPeriodo((p) => ({ ...p, inicio: e.target.value }))} data-testid="periodo-inicio" /></F>
            <F label="Data final"><input type="date" className={inputCls} value={periodo.fim} onChange={(e) => setPeriodo((p) => ({ ...p, fim: e.target.value }))} data-testid="periodo-fim" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriodoOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={emitirPdfPeriodo} data-testid="periodo-gerar"><Printer className="w-4 h-4" /> Gerar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resumo clínico */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="resumo-clinico">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-blue-600" /> Resumo Clínico</h2>
          {podeClinico
            ? <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvarInfo} data-testid="salvar-resumo-btn"><Save className="w-4 h-4" /> Salvar resumo</Button>
            : <span className="text-xs text-slate-400">Somente leitura</span>}
        </div>
        {podeClinico ? (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            <F label="Tipo sanguíneo"><input className={inputCls} value={info.tipoSanguineo || ""} onChange={(e) => setI("tipoSanguineo", e.target.value)} /></F>
            <F label="Dieta"><input className={inputCls} value={info.dieta || ""} onChange={(e) => setI("dieta", e.target.value)} /></F>
            <F label="Mobilidade"><input className={inputCls} value={info.mobilidade || ""} onChange={(e) => setI("mobilidade", e.target.value)} /></F>
            <F label="Alergias"><input className={inputCls} value={info.alergias || ""} onChange={(e) => setI("alergias", e.target.value)} /></F>
            <div className="sm:col-span-2"><F label="Diagnósticos (CID)"><input className={inputCls} value={info.diagnosticos || ""} onChange={(e) => setI("diagnosticos", e.target.value)} /></F></div>
            <div className="sm:col-span-2"><F label="Condições crônicas"><input className={inputCls} value={info.condicoesCronicas || ""} onChange={(e) => setI("condicoesCronicas", e.target.value)} /></F></div>
            <div className="sm:col-span-2"><F label="Plano de cuidados"><input className={inputCls} value={info.planoCuidados || ""} onChange={(e) => setI("planoCuidados", e.target.value)} /></F></div>
            <div className="sm:col-span-2"><F label="Observações clínicas"><input className={inputCls} value={info.observacoesClinicas || ""} onChange={(e) => setI("observacoesClinicas", e.target.value)} /></F></div>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Tipo sanguíneo", info.tipoSanguineo], ["Dieta", info.dieta], ["Mobilidade", info.mobilidade], ["Alergias", info.alergias],
              ["Diagnósticos (CID)", info.diagnosticos], ["Condições crônicas", info.condicoesCronicas],
              ["Plano de cuidados", info.planoCuidados], ["Observações clínicas", info.observacoesClinicas]].map(([label, val]) => (
              <div key={label}><p className="text-xs font-medium text-slate-400">{label}</p><p className="text-sm text-slate-700 mt-0.5">{val || "—"}</p></div>
            ))}
          </div>
        )}
      </div>

      <Tabs defaultValue={abaInicial} className="w-full">
        <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1">
          <TabsTrigger value="timeline" className="gap-1.5"><History className="w-4 h-4" /> Linha do Tempo</TabsTrigger>
          <TabsTrigger value="sinais" className="gap-1.5"><Activity className="w-4 h-4" /> Sinais Vitais</TabsTrigger>
          {podeClinico && <TabsTrigger value="medicacao" className="gap-1.5"><Pill className="w-4 h-4" /> Medicações</TabsTrigger>}
          {podeReceituario && <TabsTrigger value="receituario" className="gap-1.5"><FileSignature className="w-4 h-4" /> Receituário</TabsTrigger>}
          <TabsTrigger value="prescricao_cuidados" className="gap-1.5"><HandHeart className="w-4 h-4" /> Prescrição de Cuidados</TabsTrigger>
          <TabsTrigger value="decubito" className="gap-1.5"><RotateCcw className="w-4 h-4" /> Decúbito</TabsTrigger>
          <TabsTrigger value="anotacao" className="gap-1.5"><ClipboardList className="w-4 h-4" /> Anot. Enfermagem</TabsTrigger>
          {podeVerEvolucao && <TabsTrigger value="evolucao" className="gap-1.5"><NotebookPen className="w-4 h-4" /> Evolução</TabsTrigger>}
          <TabsTrigger value="balanco" className="gap-1.5"><Droplets className="w-4 h-4" /> Balanço Hídrico</TabsTrigger>
          <TabsTrigger value="ocorrencia" className="gap-1.5"><TriangleAlert className="w-4 h-4" /> Ocorrências</TabsTrigger>
        </TabsList>

        {/* LINHA DO TEMPO */}
        <TabsContent value="timeline" className="pt-4"><LinhaTempo registros={registros} /></TabsContent>
        {/* SINAIS VITAIS */}
        <TabsContent value="sinais" className="pt-4"><SinaisVitais lista={porTipo.sinais_vitais} onAdd={add} onRemove={remover} /></TabsContent>
        {/* MEDICAÇÕES (clínico) */}
        {podeClinico && <TabsContent value="medicacao" className="pt-4"><Medicacoes lista={porTipo.medicacao} onAdd={add} onRemove={remover} onUpdate={atualizar} /></TabsContent>}
        {/* RECEITUÁRIO (somente enfermeiro/admin) */}
        {podeReceituario && <TabsContent value="receituario" className="pt-4"><Receituario residente={res} medicacoes={porTipo.medicacao} /></TabsContent>}
        {/* PRESCRIÇÃO DE CUIDADOS (edição só clínico; demais só leitura) */}
        <TabsContent value="prescricao_cuidados" className="pt-4"><PrescricaoCuidados lista={porTipo.prescricao_cuidados} onAdd={add} onRemove={remover} readOnly={!podeClinico} /></TabsContent>
        {/* HISTÓRICO DE DECÚBITO */}
        <TabsContent value="decubito" className="pt-4"><HistoricoDecubito lista={porTipo.mudanca_decubito || []} /></TabsContent>
        {/* ANOTAÇÃO DE ENFERMAGEM */}
        <TabsContent value="anotacao" className="pt-4"><AnotacaoEnfermagem lista={porTipo.anotacao_enfermagem} onAdd={add} onRemove={remover} onUpdate={atualizar} rid={id} podeApagar={podeApagarAnotacao} podeEditarSempre={podeEditarAnotacaoSempre} /></TabsContent>
        {/* EVOLUÇÃO (profissionais de saúde inserem; todos os clínicos veem) */}
        {podeVerEvolucao && <TabsContent value="evolucao" className="pt-4"><Evolucao lista={porTipo.evolucao} onAdd={add} onRemove={remover} onUpdate={atualizar} podeInserir={podeInserirEvolucao} minhaCategoria={minhaCategoriaEvol} authId={auth?.id} nomeResidente={res?.nome} /></TabsContent>}
        {/* BALANÇO HÍDRICO */}
        <TabsContent value="balanco" className="pt-4"><BalancoHidrico lista={porTipo.balanco_hidrico} onAdd={add} onRemove={remover} /></TabsContent>
        {/* OCORRÊNCIAS */}
        <TabsContent value="ocorrencia" className="pt-4"><Ocorrencias lista={porTipo.ocorrencia} onAdd={add} onRemove={remover} /></TabsContent>
      </Tabs>
    </div>
  );
};

// ---- Sinais Vitais ----
const SinaisVitais = ({ lista, onAdd, onRemove }) => {
  const empty = { pa: "", fc: "", fr: "", temp: "", spo2: "", glicemia: "", dor: "", peso: "" };
  const [f, setF] = useState(empty);
  const s = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const salvar = () => { onAdd("sinais_vitais", f); setF(empty); };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <F label="PA (ex.: 12/8)"><input type="text" className={inputCls} value={f.pa} onChange={(e) => s("pa", e.target.value)} placeholder="12/8" data-testid="sv-pa" /></F>
          {[["fc", "FC (bpm)"], ["fr", "FR (irpm)"], ["temp", "Temp. (°C)"], ["spo2", "SpO₂ (%)"], ["glicemia", "Glicemia (mg/dL)"], ["dor", "Dor (0-10)"], ["peso", "Peso (kg)"]].map(([k, l]) => (
            <F key={k} label={l}><input type="number" step="0.1" className={inputCls} value={f[k]} onChange={(e) => s(k, e.target.value)} /></F>
          ))}
          <div className="flex items-end"><Button className="bg-blue-600 hover:bg-blue-700 gap-2 w-full" onClick={salvar}><Plus className="w-4 h-4" /> Registrar</Button></div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-400 border-b border-slate-100">
            {["Data/Hora", "PA", "FC", "FR", "Temp", "SpO₂", "Glicemia", "Dor", "Peso", "Autor", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {lista.length === 0 && <tr><td colSpan={11} className="text-center text-slate-400 py-6">Nenhum registro.</td></tr>}
            {lista.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5 text-slate-500">{fmtDT(r.data)}</td>
                <td className="px-4 py-2.5">{r.dados.pa || [r.dados.pas, r.dados.pad].filter(Boolean).join("/")}</td>
                <td className="px-4 py-2.5">{r.dados.fc}</td>
                <td className="px-4 py-2.5">{r.dados.fr}</td>
                <td className="px-4 py-2.5">{r.dados.temp}</td>
                <td className="px-4 py-2.5">{r.dados.spo2}</td>
                <td className="px-4 py-2.5">{r.dados.glicemia}</td>
                <td className="px-4 py-2.5">{r.dados.dor}</td>
                <td className="px-4 py-2.5">{r.dados.peso}</td>
                <td className="px-4 py-2.5 text-slate-500">{r.autor}</td>
                <td className="px-4 py-2.5"><button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- Medicações ----
const Medicacoes = ({ lista, onAdd, onRemove, onUpdate }) => {
  const { getMedicacaoModelos, createMedicacaoModelo, deleteMedicacaoModelo, produtos, salvarProduto, auth } = useApp();
  const { toast } = useToast();
  const empty = { medicamento: "", dose: "", unidade: "CP", via: "ORAL", horarios: "", usoContinuo: true, diasTratamento: "", observacao: "" };
  const [f, setF] = useState(empty);
  const [obsTouched, setObsTouched] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [editId, setEditId] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const s = (k, v) => setF((x) => ({ ...x, [k]: v }));

  // catálogo de medicamentos (mesma fonte do Receituário)
  const medNomes = (produtos || [])
    .filter((p) => ["Medicamento", "Medicamento Alto Custo", "Dieta Especial"].includes(p.categoria))
    .map((p) => p.nome);
  const existeMed = (nome) => medNomes.some((n) => n.trim().toLowerCase() === (nome || "").trim().toLowerCase());
  const [pergunta, setPergunta] = useState(false);          // notice "não encontrado"
  const [novoMed, setNovoMed] = useState(null);             // dados do modal de novo medicamento
  const [salvandoMed, setSalvandoMed] = useState(false);

  const conferirMed = () => setPergunta(!!f.medicamento.trim() && !existeMed(f.medicamento));
  const abrirNovoMed = () => { setNovoMed({ nome: f.medicamento.trim(), categoria: "Medicamento", unidade: "un" }); setPergunta(false); };
  const salvarNovoMed = async () => {
    if (!novoMed.nome) return;
    setSalvandoMed(true);
    try {
      await salvarProduto({ nome: novoMed.nome, categoria: novoMed.categoria, unidade: novoMed.unidade,
        precoCompra: 0, precoVenda: 0, estoqueMinimo: 0, estoqueInicial: 0 });
      toast({ title: `"${novoMed.nome}" cadastrado no sistema` });
      setF((x) => ({ ...x, medicamento: novoMed.nome }));
      setNovoMed(null);
    } catch (e) {
      toast({ title: "Não foi possível cadastrar", description: e?.response?.data?.detail || "Erro ao salvar medicamento." });
    } finally { setSalvandoMed(false); }
  };

  const carregarModelos = async () => { try { setModelos(await getMedicacaoModelos()); } catch (e) { /* noop */ } };
  useEffect(() => { carregarModelos(); /* eslint-disable-next-line */ }, []);

  const salvarComoModelo = async () => {
    if (!f.medicamento) { toast({ title: "Preencha o medicamento antes de salvar o modelo" }); return; }
    const nome = window.prompt("Nome do modelo de medicação:", f.medicamento);
    if (!nome) return;
    await createMedicacaoModelo({ nome, dados: { ...f, observacao: f.observacao || montarObsMed(f) } });
    toast({ title: "Modelo de medicação salvo" });
    carregarModelos();
  };
  const carregarModelo = (mid) => {
    const m = modelos.find((x) => x.id === mid);
    if (!m) return;
    setObsTouched(true); // preserva a observação salva no modelo
    setF({ ...empty, ...m.dados });
    toast({ title: `Modelo "${m.nome}" carregado` });
  };
  const removerModelo = async (mid) => { await deleteMedicacaoModelo(mid); carregarModelos(); };

  // recompõe a observação automaticamente enquanto os campos são preenchidos (a menos que editada manualmente)
  useEffect(() => {
    if (obsTouched) return;
    setF((x) => ({ ...x, observacao: montarObsMed(x) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.dose, f.unidade, f.via, f.horarios, f.usoContinuo, f.diasTratamento, obsTouched]);

  const salvar = () => {
    if (!f.medicamento) return;
    const dados = { ...f, observacao: f.observacao || montarObsMed(f) };
    const agora = new Date().toISOString();
    if (editId) {
      const hist = Array.isArray(f._hist) ? f._hist : [];
      onUpdate(editId, { ...dados, _hist: [...hist, { por: auth?.nome || "—", em: agora }] });
      toast({ title: "Medicação atualizada" });
    } else {
      onAdd("medicacao", dados);
    }
    setF(empty); setObsTouched(false); setEditId(null);
  };
  const editar = (r) => {
    setF({ ...empty, ...r.dados });
    setObsTouched(true);
    setEditId(r.id);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelarEdicao = () => { setF(empty); setObsTouched(false); setEditId(null); };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <F label="Medicamento">
          <input className={inputCls} list="med-cat-list" value={f.medicamento} onChange={(e) => { s("medicamento", e.target.value); if (pergunta) setPergunta(false); }} onBlur={conferirMed} placeholder="Digite p/ buscar no cadastro" data-testid="med-medicamento" />
          <datalist id="med-cat-list">{medNomes.map((n) => <option key={n} value={n} />)}</datalist>
        </F>
        <F label="Dose"><input className={inputCls} placeholder="ex.: 1" value={f.dose} onChange={(e) => s("dose", e.target.value)} data-testid="med-dose" /></F>
        <F label="Unidade / apresentação">
          <Select value={f.unidade} onValueChange={(v) => s("unidade", v)}>
            <SelectTrigger data-testid="med-unidade"><SelectValue /></SelectTrigger>
            <SelectContent>{UNIDADES.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Via de administração">
          <Select value={f.via} onValueChange={(v) => s("via", v)}>
            <SelectTrigger data-testid="med-via"><SelectValue /></SelectTrigger>
            <SelectContent>{VIAS_ADMIN.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Horários">
          <input className={inputCls} list="med-horarios-list" placeholder="ex.: 06 e 18 (digite p/ buscar)" value={f.horarios} onChange={(e) => s("horarios", e.target.value)} data-testid="med-horarios" />
          <datalist id="med-horarios-list">{HORARIOS_OPCOES.map((h) => <option key={h} value={h} />)}</datalist>
        </F>
        <F label="Dias de tratamento">
          <input type="number" min="1" className={`${inputCls} ${f.usoContinuo ? "bg-slate-100 text-slate-400" : ""}`} placeholder="ex.: 20" value={f.diasTratamento} disabled={f.usoContinuo} onChange={(e) => s("diasTratamento", e.target.value)} data-testid="med-dias-tratamento" />
        </F>
        <div className="sm:col-span-3 flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={f.usoContinuo} onChange={(e) => { const c = e.target.checked; setF((x) => ({ ...x, usoContinuo: c, diasTratamento: c ? "" : x.diasTratamento })); }} data-testid="med-uso-continuo" />
            Uso contínuo
          </label>
        </div>
        <div className="sm:col-span-3">
          <F label="Observação (preenchida automaticamente — editável)">
            <textarea rows={2} className={inputCls} value={f.observacao} onChange={(e) => { setObsTouched(true); s("observacao", e.target.value); }} data-testid="med-observacao" />
          </F>
        </div>
        {pergunta && (
          <div className="sm:col-span-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="med-nao-encontrado">
            <span className="text-sm text-amber-800">"{f.medicamento}" não está no cadastro. Deseja incluir este medicamento?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPergunta(false)}>Não</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={abrirNovoMed} data-testid="med-incluir-sim">Sim, cadastrar</Button>
            </div>
          </div>
        )}
        <div className="sm:col-span-3 flex flex-wrap items-center gap-2 border border-dashed border-slate-200 rounded-lg p-3">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> Modelos:</span>
          <div className="min-w-[200px]">
            <Select value="" onValueChange={carregarModelo}>
              <SelectTrigger data-testid="med-modelo-carregar"><SelectValue placeholder={modelos.length ? "Carregar modelo..." : "Nenhum modelo salvo"} /></SelectTrigger>
              <SelectContent>{modelos.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={salvarComoModelo} data-testid="med-modelo-salvar"><BookmarkPlus className="w-3.5 h-3.5" /> Salvar como modelo</Button>
          {modelos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {modelos.map((m) => (
                <span key={m.id} className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-1">
                  {m.nome}<button onClick={() => removerModelo(m.id)} className="text-slate-400 hover:text-red-500" data-testid={`med-modelo-excluir-${m.id}`}><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="sm:col-span-3 flex items-center gap-2">
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} data-testid="med-adicionar"><Plus className="w-4 h-4" /> {editId ? "Salvar alterações" : "Adicionar medicação"}</Button>
          {editId && <Button variant="outline" onClick={cancelarEdicao} data-testid="med-cancelar-edicao">Cancelar edição</Button>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {lista.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma medicação prescrita.</p>}
        {lista.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><Pill className="w-4 h-4 text-blue-600" /><p className="font-semibold text-slate-800">{r.dados.medicamento}</p></div>
              <div className="flex items-center gap-2">
                <button onClick={() => setAuditItem(r)} className="text-slate-300 hover:text-blue-600" title="Auditoria" data-testid={`med-auditoria-${r.id}`}><History className="w-4 h-4" /></button>
                <button onClick={() => editar(r)} className="text-slate-300 hover:text-blue-600" title="Editar" data-testid={`med-editar-${r.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500" title="Excluir"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-1">{[r.dados.dose, r.dados.unidade].filter(Boolean).join(" ")} · {cap(r.dados.via)} · {r.dados.horarios || r.dados.frequencia}</p>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {r.dados.usoContinuo && <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">Uso contínuo</span>}
              {!r.dados.usoContinuo && r.dados.diasTratamento && <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{r.dados.diasTratamento} dias</span>}
            </div>
            {r.dados.observacao && <p className="text-xs text-slate-500 mt-1">Obs.: {r.dados.observacao}</p>}
            <p className="text-[11px] text-slate-400 mt-2">{fmtDT(r.data)} · {r.autor}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!novoMed} onOpenChange={(o) => !o && setNovoMed(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="novo-medicamento-modal">
          <DialogHeader><DialogTitle>Cadastrar novo medicamento</DialogTitle></DialogHeader>
          {novoMed && (
            <div className="space-y-3">
              <F label="Nome do medicamento">
                <input className={inputCls} value={novoMed.nome} onChange={(e) => setNovoMed((x) => ({ ...x, nome: e.target.value }))} data-testid="novo-med-nome" />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Categoria">
                  <Select value={novoMed.categoria} onValueChange={(v) => setNovoMed((x) => ({ ...x, categoria: v }))}>
                    <SelectTrigger data-testid="novo-med-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Medicamento", "Medicamento Alto Custo", "Dieta Especial"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Unidade">
                  <Select value={novoMed.unidade} onValueChange={(v) => setNovoMed((x) => ({ ...x, unidade: v }))}>
                    <SelectTrigger data-testid="novo-med-unidade"><SelectValue /></SelectTrigger>
                    <SelectContent>{["un", "cp", "caps", "ml", "mg", "amp", "fr", "gotas", "sache"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoMed(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!novoMed?.nome || salvandoMed} onClick={salvarNovoMed} data-testid="novo-med-salvar">
              {salvandoMed ? "Salvando..." : "Salvar medicamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!auditItem} onOpenChange={(o) => !o && setAuditItem(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="med-auditoria-modal">
          <DialogHeader><DialogTitle>Auditoria da medicação</DialogTitle></DialogHeader>
          {auditItem && (
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-slate-800">{auditItem.dados.medicamento}</p>
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1">
                <p><span className="text-slate-500">Inserido por:</span> <b>{auditItem.autor || "—"}</b></p>
                <p><span className="text-slate-500">Data/hora do registro:</span> <b>{fmtDT(auditItem.data)}</b></p>
              </div>
              {Array.isArray(auditItem.dados._hist) && auditItem.dados._hist.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 space-y-1">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Edições</p>
                  {auditItem.dados._hist.map((h, i) => (
                    <p key={i} className="text-xs text-slate-600">Editado por <b>{h.por}</b> em {fmtDT(h.em)}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAuditItem(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const CATEGORIAS_EVOL = ["Enfermagem", "Médica", "Fisioterapia", "Nutrição", "Psicologia", "Multidisciplinar"];
const Evolucao = ({ lista, onAdd, onRemove, onUpdate, podeInserir, minhaCategoria, authId, nomeResidente }) => {
  const [texto, setTexto] = useState("");
  const [filtro, setFiltro] = useState("Todas");
  const [editId, setEditId] = useState(null);
  const [editTexto, setEditTexto] = useState("");
  const salvar = () => { if (!texto.trim()) return; onAdd("evolucao", { categoria: minhaCategoria, texto }); setTexto(""); };
  const salvarEdicao = (r) => { if (!editTexto.trim()) return; onUpdate(r.id, { ...r.dados, texto: editTexto }); setEditId(null); setEditTexto(""); };
  const [deEv, setDeEv] = useState("");
  const [ateEv, setAteEv] = useState("");
  const visiveis = lista
    .filter((r) => filtro === "Todas" || r.dados?.categoria === filtro)
    .filter((r) => (!deEv || (r.data || "").slice(0, 10) >= deEv) && (!ateEv || (r.data || "").slice(0, 10) <= ateEv));
  const baixarPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text(`Evoluções — ${nomeResidente || ""}`, 14, 16);
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text(deEv || ateEv ? `Período: ${deEv ? formatDate(deEv) : "início"} a ${ateEv ? formatDate(ateEv) : "hoje"}` : "Todas as evoluções registradas", 14, 22);
    doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")}`, 14, 27);
    autoTable(doc, {
      startY: 32, styles: { fontSize: 8.5, cellPadding: 2.5, valign: "top" }, headStyles: { fillColor: [30, 64, 175] },
      columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 30 }, 3: { cellWidth: 34 } },
      head: [["Data/Hora", "Categoria", "Evolução", "Profissional"]],
      body: [...visiveis].sort((a, b) => (a.data || "").localeCompare(b.data || "")).map((r) => [
        (r.data || "").replace("T", " ").slice(0, 16).split(" ").map((x, i) => i === 0 ? x.split("-").reverse().join("/") : x).join(" "),
        r.dados?.categoria || "", r.dados?.texto || "", r.autor || ""]),
    });
    doc.save(`evolucoes-${(nomeResidente || "residente").replace(/\s+/g, "_")}${deEv || ateEv ? `-${deEv || ""}_${ateEv || ""}` : ""}.pdf`);
  };
  return (
    <div className="space-y-4">
      {podeInserir ? (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-3" data-testid="evolucao-form">
          <div className="text-sm text-slate-600">Categoria (definida pelo seu perfil): <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded" data-testid="evolucao-categoria-auto">{minhaCategoria}</span></div>
          <F label="Evolução"><textarea rows={3} className={inputCls} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Descreva a evolução do residente..." data-testid="evolucao-texto" /></F>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} data-testid="evolucao-adicionar"><Plus className="w-4 h-4" /> Adicionar evolução</Button>
        </div>
      ) : (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3" data-testid="evolucao-sem-permissao">
          Somente enfermeiro(a), médico(a), nutricionista e fisioterapeuta registram evolução. Você pode visualizar todas as evoluções abaixo.
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-500">Ver categoria:</span>
        <div className="w-56"><Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger data-testid="evolucao-filtro"><SelectValue /></SelectTrigger>
          <SelectContent>{["Todas", ...CATEGORIAS_EVOL].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select></div>
        <span className="text-xs font-semibold text-slate-500 ml-2">Período:</span>
        <input type="date" value={deEv} onChange={(e) => setDeEv(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid="evolucao-de" />
        <span className="text-xs text-slate-400">até</span>
        <input type="date" value={ateEv} onChange={(e) => setAteEv(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid="evolucao-ate" />
        {(deEv || ateEv) && <Button variant="ghost" size="sm" onClick={() => { setDeEv(""); setAteEv(""); }} data-testid="evolucao-limpar-periodo">Limpar</Button>}
        <Button variant="outline" size="sm" className="ml-auto gap-1.5" onClick={baixarPdf} disabled={visiveis.length === 0} data-testid="evolucao-pdf">
          <Download className="w-4 h-4" /> Baixar PDF {deEv || ateEv ? "(período)" : "(todas)"}
        </Button>
      </div>
      <div className="space-y-3">
        {visiveis.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma evolução registrada.</p>}
        {visiveis.map((r) => {
          const meu = r.dados?.autorId && r.dados.autorId === authId;
          return (
            <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid={`evolucao-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{r.dados.categoria}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-slate-400">{fmtDT(r.data)} · {r.autor}</span>
                  {meu && editId !== r.id && <button onClick={() => { setEditId(r.id); setEditTexto(r.dados.texto || ""); }} className="text-slate-300 hover:text-blue-600" title="Editar" data-testid={`evolucao-editar-${r.id}`}><Pencil className="w-4 h-4" /></button>}
                  {meu && <button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500" title="Excluir" data-testid={`evolucao-excluir-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              {editId === r.id ? (
                <div className="mt-2 space-y-2">
                  <textarea rows={3} className={inputCls} value={editTexto} onChange={(e) => setEditTexto(e.target.value)} data-testid={`evolucao-edit-texto-${r.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => salvarEdicao(r)} data-testid={`evolucao-edit-salvar-${r.id}`}>Salvar</Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditId(null); setEditTexto(""); }}>Cancelar</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.dados.texto}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- Histórico de Decúbito (registro visual das mudanças de posição) ----
const HistoricoDecubito = ({ lista }) => {
  const hoje = new Date().toISOString().slice(0, 10);
  const ordenada = [...lista].sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  const doDia = ordenada.filter((r) => (r.data || "").startsWith(hoje));
  const posicoesHoje = new Set(doDia.map((r) => r.dados?.posicao));
  const TODAS = ["Decúbito Dorsal", "Decúbito Lateral Esquerdo", "Decúbito Lateral Direito"];
  return (
    <div className="space-y-4" data-testid="historico-decubito">
      {/* Resumo do dia */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><RotateCcw className="w-4 h-4 text-indigo-600" /> Mudanças de hoje</h3>
          <span className="text-sm font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-md" data-testid="decubito-hoje-total">{doDia.length} registro(s)</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          {TODAS.map((p) => (
            <div key={p} className={`rounded-lg border p-2 text-center ${posicoesHoje.has(p) ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-slate-50"}`}>
              <img src={IMG_DECUBITO[p]} alt={p} className="w-full h-20 object-contain" />
              <p className="text-[11px] font-medium text-slate-600 mt-1">{p}</p>
              <p className={`text-[10px] font-semibold ${posicoesHoje.has(p) ? "text-emerald-600" : "text-slate-400"}`}>{posicoesHoje.has(p) ? "✓ realizada" : "pendente"}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Linha do tempo das mudanças */}
      <div className="space-y-2">
        {ordenada.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma mudança de decúbito registrada.</p>}
        {ordenada.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 flex items-center gap-3" data-testid={`decubito-registro-${r.id}`}>
            <img src={IMG_DECUBITO[r.dados?.posicao]} alt={r.dados?.posicao} className="w-16 h-16 rounded-lg object-contain bg-slate-50 border border-slate-100" />
            <div className="flex-1">
              <p className="font-semibold text-slate-800">{r.dados?.posicao || "-"}</p>
              <p className="text-xs text-slate-400 mt-0.5">{fmtDT(r.data)} · {r.autor}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ---- Ocorrências ----
const Ocorrencias = ({ lista, onAdd, onRemove }) => {
  const [grav, setGrav] = useState("Leve");
  const [texto, setTexto] = useState("");
  const salvar = () => { if (!texto.trim()) return; onAdd("ocorrencia", { gravidade: grav, texto }); setTexto(""); };
  const cor = { Leve: "text-emerald-700 bg-emerald-50", Moderada: "text-amber-700 bg-amber-50", Grave: "text-red-700 bg-red-50" };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-3">
        <div className="w-56"><F label="Gravidade">
          <Select value={grav} onValueChange={setGrav}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["Leve", "Moderada", "Grave"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </F></div>
        <F label="Descrição da ocorrência"><textarea rows={3} className={inputCls} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Descreva a intercorrência..." /></F>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar}><Plus className="w-4 h-4" /> Registrar ocorrência</Button>
      </div>
      <div className="space-y-3">
        {lista.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma ocorrência registrada.</p>}
        {lista.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cor[r.dados.gravidade] || "text-slate-600 bg-slate-100"}`}>{r.dados.gravidade}</span>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-slate-400">{fmtDT(r.data)} · {r.autor}</span>
                <button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.dados.texto}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Receituário / Prescrição Médica (enfermeiro e administrador) ----
const emptyMed = () => ({ nome: "", concentracao: "", quantidade: "", posologia: "" });

const Receituario = ({ residente, medicacoes = [] }) => {
  const { getProfissionais, getReceituarios, createReceituario, deleteReceituario,
    getReceitaModelos, createReceitaModelo, deleteReceitaModelo, empresa, auth } = useApp();
  // converte uma medicação do prontuário para o formato do receituário
  const medToReceita = (m) => ({
    nome: m.medicamento || "",
    concentracao: [m.dose, m.unidade].filter(Boolean).join(" "),
    quantidade: "",
    posologia: m.observacao || montarObsMed(m),
  });
  const medsDoProntuario = (medicacoes || []).map((r) => medToReceita(r.dados || {}));
  const { toast } = useToast();
  const [profissionais, setProfissionais] = useState([]);
  const [lista, setLista] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [profId, setProfId] = useState("");
  const [usoContinuo, setUsoContinuo] = useState(true);
  const [descritivo, setDescritivo] = useState("");
  const [medsList, setMedsList] = useState(medsDoProntuario);
  const [checked, setChecked] = useState(medsDoProntuario.map(() => true));
  const [dataHora, setDataHora] = useState(agoraISO());
  const [editRecId, setEditRecId] = useState(null);
  const [editHist, setEditHist] = useState(null);
  const [auditRec, setAuditRec] = useState(null);

  // médicos ativos cadastrados (fallback: qualquer profissional ativo)
  const medicosAtivos = profissionais.filter((p) => p.ativo !== false && /m[ée]dic/i.test(p.categoria || ""));
  const prescritores = medicosAtivos.length ? medicosAtivos : profissionais.filter((p) => p.ativo !== false);
  const unicoMedico = prescritores.length === 1;

  const puxarMedicacoes = () => {
    if (!medsDoProntuario.length) { toast({ title: "Nenhuma medicação no prontuário para puxar" }); return; }
    setMedsList(medsDoProntuario);
    setChecked(medsDoProntuario.map(() => true));
    toast({ title: `${medsDoProntuario.length} medicação(ões) carregada(s) do prontuário` });
  };
  const allChecked = medsList.length > 0 && checked.every(Boolean);
  const toggleAll = (v) => setChecked(medsList.map(() => v));
  const toggleOne = (i) => setChecked((c) => c.map((x, idx) => (idx === i ? !x : x)));
  const medsChecked = medsList.filter((_, i) => checked[i]);

  const carregar = async () => {
    const [p, r, m] = await Promise.all([getProfissionais(), getReceituarios(residente.id), getReceitaModelos()]);
    setProfissionais(p); setLista(r); setModelos(m);
    const med = p.filter((x) => x.ativo !== false && /m[ée]dic/i.test(x.categoria || ""));
    const presc = med.length ? med : p.filter((x) => x.ativo !== false);
    setProfId((cur) => cur || (presc[0] ? presc[0].id : ""));
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [residente.id]);

  const prof = profissionais.find((p) => p.id === profId);

  const carregarModelo = (mid) => {
    const m = modelos.find((x) => x.id === mid);
    if (!m) return;
    const list = (m.medicamentos && m.medicamentos.length) ? m.medicamentos.map((x) => ({ ...emptyMed(), ...x })) : [];
    setMedsList(list); setChecked(list.map(() => true));
    setDescritivo(m.descritivo || "");
    setUsoContinuo(!!m.usoContinuo);
    toast({ title: `Modelo "${m.nome}" carregado` });
  };
  const salvarModelo = async () => {
    const validMeds = medsChecked.filter((x) => x.nome.trim());
    if (!validMeds.length) { toast({ title: "Marque medicamentos antes de salvar o modelo" }); return; }
    const nome = window.prompt("Nome do modelo de receita:");
    if (!nome) return;
    await createReceitaModelo({ nome, usoContinuo, descritivo, medicamentos: validMeds });
    toast({ title: "Modelo salvo" });
    carregar();
  };
  const removerModelo = async (mid) => { await deleteReceitaModelo(mid); carregar(); };

  const salvar = async () => {
    if (!prof) { toast({ title: "Selecione um profissional prescritor" }); return; }
    const validMeds = medsChecked.filter((m) => m.nome.trim());
    if (!validMeds.length && !descritivo.trim()) { toast({ title: "Marque medicamentos ou escreva a receita descritiva" }); return; }
    const dados = {
      profissionalId: prof.id, profissionalNome: prof.nome, conselho: prof.conselho,
      registro: prof.registro, registroUf: prof.registroUf, especialidade: prof.especialidade,
      usoContinuo, descritivo, medicamentos: validMeds,
      _hist: [...(editHist || []), ...(editRecId ? [{ por: autorAtual(), em: new Date().toISOString() }] : [])],
    };
    const autor = `${prof.nome} (${prof.conselho} ${prof.registro}${prof.registroUf ? "-" + prof.registroUf : ""})`;
    await createReceituario(residente.id, { data: dataHora || agoraISO(), autor, dados });
    if (editRecId) { await deleteReceituario(residente.id, editRecId); }
    toast({ title: editRecId ? "Receituário atualizado" : "Receituário salvo", description: "Agora você pode imprimir." });
    setDescritivo(""); setUsoContinuo(true); setMedsList(medsDoProntuario); setChecked(medsDoProntuario.map(() => true)); setDataHora(agoraISO());
    setEditRecId(null); setEditHist(null);
    carregar();
  };
  const remover = async (id) => { await deleteReceituario(residente.id, id); carregar(); };
  const autorAtual = () => (auth ? [auth.nome, auth.funcao].filter(Boolean).join(" — ") : "—");
  const editarReceituario = (rec) => {
    setProfId(rec.dados.profissionalId || "");
    setUsoContinuo(!!rec.dados.usoContinuo);
    setDescritivo(rec.dados.descritivo || "");
    const list = (rec.dados.medicamentos || []).map((m) => ({ ...emptyMed(), ...m }));
    setMedsList(list); setChecked(list.map(() => true));
    setDataHora(rec.data || agoraISO());
    setEditRecId(rec.id);
    setEditHist(Array.isArray(rec.dados._hist) ? rec.dados._hist : []);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    toast({ title: "Editando receituário", description: "Altere e clique em Salvar." });
  };
  const cancelarEdicaoRec = () => {
    setDescritivo(""); setUsoContinuo(true); setMedsList(medsDoProntuario); setChecked(medsDoProntuario.map(() => true)); setDataHora(agoraISO());
    setEditRecId(null); setEditHist(null);
  };

  const [imprimirReq, setImprimirReq] = useState(null);   // { rec, formato }
  const [medicoPrintId, setMedicoPrintId] = useState("");
  const dadosMedico = (p) => (p ? {
    profissionalId: p.id, profissionalNome: p.nome, conselho: p.conselho,
    registro: p.registro, registroUf: p.registroUf, especialidade: p.especialidade,
  } : {});
  const doPrint = (rec, formato, medico) => {
    const dados = { ...rec.dados, ...(medico ? dadosMedico(medico) : {}) };
    // a data da receita impressa é sempre a de HOJE (não a de criação)
    const payload = { empresa, residente, rec: { ...dados, data: new Date().toISOString() } };
    if (formato === "simples") gerarReceitaSimples(payload);
    else if (formato === "2vias") gerarReceita2Vias(payload);
    else gerarReceitaEspecial(payload);
  };
  const imprimir = (rec, formato) => {
    if (prescritores.length <= 1) {
      doPrint(rec, formato, prescritores[0] || null);
      return;
    }
    // mais de um médico ativo: perguntar quem fez a prescrição
    setMedicoPrintId(rec.dados.profissionalId || profId || prescritores[0].id);
    setImprimirReq({ rec, formato });
  };
  const confirmarImpressao = () => {
    if (!imprimirReq) return;
    const medico = prescritores.find((p) => p.id === medicoPrintId) || prescritores[0];
    doPrint(imprimirReq.rec, imprimirReq.formato, medico);
    setImprimirReq(null);
  };
  // Imprime direto do formulário: usa os medicamentos MARCADOS; nenhum marcado = receita em branco.
  const imprimirDireto = (formato) => {
    const rec = { dados: { usoContinuo, descritivo, medicamentos: medsChecked } };
    imprimir(rec, formato);
  };

  // Prescrição Médica mensal (grade de checagem por horário)
  const nowRef = new Date();
  const [showPresc, setShowPresc] = useState(false);
  const [prescIndividual, setPrescIndividual] = useState(false);
  const [prescSel, setPrescSel] = useState([]);
  const [prescMes, setPrescMes] = useState(`${nowRef.getFullYear()}-${String(nowRef.getMonth() + 1).padStart(2, "0")}`);
  const abrirPresc = (individual) => {
    setPrescIndividual(individual);
    setPrescSel([]);
    setShowPresc(true);
  };
  const togglePrescSel = (id) => setPrescSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const medsPresc = prescIndividual ? medicacoes.filter((r) => prescSel.includes(r.id)) : medicacoes;
  const gerarPrescMensal = () => {
    if (prescIndividual && !medsPresc.length) return;
    const [a, m] = prescMes.split("-").map(Number);
    const medico = prof || prescritores[0] || {};
    gerarPrescricaoMensal({
      empresa, residente, medicacoes: medsPresc,
      profissional: { nome: medico.nome, registro: medico.registro },
      ano: a, mes: m,
    });
    setShowPresc(false);
  };

  return (
    <div className="space-y-4" data-testid="receituario-section">
      {profissionais.length === 0 && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
          Nenhum profissional prescritor cadastrado. Peça ao administrador para cadastrar os médicos em <b>Profissionais</b> (menu lateral).
        </div>
      )}

      {/* Novo receituário */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <F label="Profissional prescritor (médico)">
              {unicoMedico ? (
                <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700" data-testid="receituario-prescritor-unico">
                  {prescritores[0].nome} — {prescritores[0].conselho} {prescritores[0].registro}{prescritores[0].registroUf ? `-${prescritores[0].registroUf}` : ""}
                </div>
              ) : (
                <Select value={profId} onValueChange={setProfId}>
                  <SelectTrigger data-testid="receituario-prescritor"><SelectValue placeholder="Selecione o médico..." /></SelectTrigger>
                  <SelectContent>
                    {prescritores.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nome} — {p.conselho} {p.registro}{p.registroUf ? `-${p.registroUf}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </F>
          </div>
          <F label="Data / Hora">
            <input type="datetime-local" className={inputCls} value={dataHora} onChange={(e) => setDataHora(e.target.value)} data-testid="receituario-data" />
          </F>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600 pb-2.5 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={usoContinuo} onChange={(e) => setUsoContinuo(e.target.checked)} data-testid="receituario-uso-continuo" />
            Uso contínuo
          </label>
        </div>

        {/* Modelos de receita reaproveitáveis */}
        <div className="flex flex-wrap items-center gap-2 border border-dashed border-slate-200 rounded-lg p-3">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> Modelos:</span>
          <div className="min-w-[200px]">
            <Select value="" onValueChange={carregarModelo}>
              <SelectTrigger data-testid="modelo-carregar"><SelectValue placeholder={modelos.length ? "Carregar modelo..." : "Nenhum modelo salvo"} /></SelectTrigger>
              <SelectContent>
                {modelos.map((m) => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={salvarModelo} data-testid="modelo-salvar"><BookmarkPlus className="w-3.5 h-3.5" /> Salvar como modelo</Button>
          {modelos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {modelos.map((m) => (
                <span key={m.id} className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-1">
                  {m.nome}<button onClick={() => removerModelo(m.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2.5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="med-select-all">
              <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} data-testid="med-select-all-input" />
              Medicamentos do prontuário — marque os que devem sair na receita
            </label>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={puxarMedicacoes} data-testid="puxar-medicacoes-btn">
              <Pill className="w-3.5 h-3.5" /> Puxar medicações do prontuário
            </Button>
          </div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-auto" data-testid="med-checklist">
            {medsList.length === 0 && (
              <p className="text-sm text-slate-400 px-3 py-3">Nenhuma medicação carregada. A receita será impressa em branco para preenchimento manual à caneta.</p>
            )}
            {medsList.map((m, i) => (
              <label key={i} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`med-check-${i}`}>
                <input type="checkbox" className="w-4 h-4 mt-0.5 accent-blue-600" checked={!!checked[i]} onChange={() => toggleOne(i)} data-testid={`med-check-input-${i}`} />
                <span className="text-sm">
                  <span className="font-medium text-slate-700">{[m.nome, m.concentracao].filter(Boolean).join(" ") || "—"}</span>
                  {m.posologia ? <span className="text-slate-500"> · {m.posologia}</span> : null}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500">Receituário descritivo (receita de cuidados / prescrição)</label>
          <RichTextEditor value={descritivo} onChange={setDescritivo} placeholder="Escreva aqui a receita de cuidados ou a prescrição... (use negrito, itálico e listas)" testid="receituario-descritivo" />
        </div>

        {/* Imprimir direto: usa os medicamentos marcados; nenhum marcado = receita em branco */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-xs font-semibold text-slate-500 w-full sm:w-auto">Imprimir ({medsChecked.length} marcado(s)):</span>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => imprimirDireto("simples")} data-testid="form-print-simples"><Printer className="w-3.5 h-3.5" /> Receita Simples</Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => imprimirDireto("2vias")} data-testid="form-print-2vias"><Copy className="w-3.5 h-3.5" /> 2 Vias</Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => imprimirDireto("especial")} data-testid="form-print-especial"><ShieldAlert className="w-3.5 h-3.5" /> Especial</Button>
          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => abrirPresc(false)} data-testid="btn-prescricao-medica"><ClipboardList className="w-3.5 h-3.5" /> Prescrição Mensal</Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => abrirPresc(true)} data-testid="btn-prescricao-individual"><ListChecks className="w-3.5 h-3.5" /> Prescrição Individual</Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} data-testid="receituario-salvar"><Save className="w-4 h-4" /> {editRecId ? "Salvar alterações" : "Salvar receituário"}</Button>
          {editRecId && <Button variant="outline" onClick={cancelarEdicaoRec} data-testid="rec-cancelar-edicao">Cancelar edição</Button>}
        </div>
      </div>

      {/* Receituários emitidos */}
      <div className="space-y-3">
        {lista.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhum receituário emitido.</p>}
        {lista.map((rec) => (
          <div key={rec.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid={`receituario-item-${rec.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-blue-600" />
                  <p className="font-semibold text-slate-800">{rec.dados.profissionalNome}</p>
                  <span className="text-xs text-slate-400">{rec.dados.conselho} {rec.dados.registro}{rec.dados.registroUf ? `-${rec.dados.registroUf}` : ""}</span>
                  {rec.dados.usoContinuo && <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">Uso contínuo</span>}
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">{fmtDT(rec.data)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setAuditRec(rec)} className="text-slate-300 hover:text-blue-600" title="Auditoria" data-testid={`rec-auditoria-${rec.id}`}><History className="w-4 h-4" /></button>
                <button onClick={() => editarReceituario(rec)} className="text-slate-300 hover:text-blue-600" title="Editar" data-testid={`rec-editar-${rec.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remover(rec.id)} className="text-slate-300 hover:text-red-500" title="Excluir"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <ul className="mt-2 space-y-1">
              {(rec.dados.medicamentos || []).map((m, i) => (
                <li key={i} className="text-sm text-slate-600">
                  <span className="font-medium text-slate-700">{[m.nome, m.concentracao].filter(Boolean).join(" ")}</span>
                  {m.quantidade ? ` — ${m.quantidade}` : ""}{m.posologia ? ` · ${m.posologia}` : ""}
                </li>
              ))}
            </ul>
            {rec.dados.descritivo && <p className="text-xs text-slate-500 mt-1 italic">{rec.dados.descritivo}</p>}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-50">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => imprimir(rec, "simples")} data-testid={`print-simples-${rec.id}`}><Printer className="w-3.5 h-3.5" /> Receita Simples</Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => imprimir(rec, "2vias")} data-testid={`print-2vias-${rec.id}`}><Copy className="w-3.5 h-3.5" /> Receituário 2 Vias</Button>
              <Button variant="outline" size="sm" className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => imprimir(rec, "especial")} data-testid={`print-especial-${rec.id}`}><ShieldAlert className="w-3.5 h-3.5" /> Receituário Especial</Button>
            </div>
          </div>
        ))}
      </div>

      {/* Diálogo: escolher médico prescritor na impressão (quando há mais de um médico ativo) */}
      <Dialog open={!!imprimirReq} onOpenChange={(o) => !o && setImprimirReq(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="print-medico-modal">
          <DialogHeader><DialogTitle>Quem fez a prescrição?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Selecione o médico responsável por esta receita. A data impressa será a de hoje.</p>
          <Select value={medicoPrintId} onValueChange={setMedicoPrintId}>
            <SelectTrigger data-testid="print-medico-select"><SelectValue placeholder="Selecione o médico..." /></SelectTrigger>
            <SelectContent>
              {prescritores.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.nome} — {p.conselho} {p.registro}{p.registroUf ? `-${p.registroUf}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImprimirReq(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirmarImpressao} data-testid="print-medico-confirmar">
              <Printer className="w-4 h-4 mr-1.5" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPresc} onOpenChange={setShowPresc}>
        <DialogContent aria-describedby={undefined} className={prescIndividual ? "sm:max-w-lg" : "sm:max-w-md"} data-testid={prescIndividual ? "prescricao-individual-modal" : "prescricao-mensal-modal"}>
          <DialogHeader><DialogTitle>{prescIndividual ? "Prescrição Individual" : "Prescrição Mensal"}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            {prescIndividual
              ? "Marque quais medicações entram nesta prescrição. O PDF segue o mesmo modelo da prescrição mensal (grade de checagem por horário e dia), apenas com as medicações selecionadas."
              : "Escolha o mês que vai no cabeçalho. O sistema gera um PDF em paisagem com a grade de checagem de todas as medicações do residente, organizada por horário (uma linha por horário de cada medicação) e com um quadradinho para cada dia do mês."}
          </p>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500">Mês da prescrição</label>
            <input type="month" className={inputCls} value={prescMes} onChange={(e) => setPrescMes(e.target.value)} data-testid="prescricao-mensal-mes" />
          </div>
          {prescIndividual && (
            <div className="space-y-2" data-testid="prescricao-individual-lista">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Medicações ({prescSel.length} de {medicacoes.length} marcada(s))</span>
                <div className="flex gap-1">
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setPrescSel(medicacoes.map((r) => r.id))} data-testid="prescricao-individual-marcar-todas">Marcar todas</button>
                  <span className="text-xs text-slate-300">|</span>
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setPrescSel([])} data-testid="prescricao-individual-desmarcar">Desmarcar</button>
                </div>
              </div>
              {medicacoes.length === 0 ? (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg px-3 py-4 text-center" data-testid="prescricao-individual-vazio">Nenhuma medicação ativa cadastrada no prontuário.</div>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {medicacoes.map((r) => {
                    const d = r.dados || {}; const on = prescSel.includes(r.id);
                    return (
                      <label key={r.id} className={`flex items-start gap-3 px-3 py-2 cursor-pointer text-sm ${on ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`prescricao-individual-item-${r.id}`}>
                        <input type="checkbox" className="mt-1 accent-emerald-600" checked={on} onChange={() => togglePrescSel(r.id)} data-testid={`prescricao-individual-check-${r.id}`} />
                        <span className="flex-1 min-w-0">
                          <span className="font-medium text-slate-800">{d.medicamento || "Medicação"}</span>
                          {d.dose && <span className="text-slate-600"> — {d.dose}{d.unidade ? ` ${d.unidade}` : ""}</span>}
                          <span className="block text-xs text-slate-500 truncate">
                            {[d.via && `Via ${String(d.via).toLowerCase()}`, d.horarios && `Horários: ${d.horarios}`].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPresc(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={gerarPrescMensal} disabled={prescIndividual && !prescSel.length} data-testid="prescricao-mensal-gerar">
              <ClipboardList className="w-4 h-4 mr-1.5" /> Gerar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!auditRec} onOpenChange={(o) => !o && setAuditRec(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="rec-auditoria-modal">
          <DialogHeader><DialogTitle>Auditoria do receituário</DialogTitle></DialogHeader>
          {auditRec && (
            <div className="space-y-2 text-sm">
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1">
                <p><span className="text-slate-500">Criado por:</span> <b>{auditRec.autor || auditRec.dados?.profissionalNome || "—"}</b></p>
                <p><span className="text-slate-500">Data/hora:</span> <b>{fmtDT(auditRec.data)}</b></p>
                <p><span className="text-slate-500">Medicamentos:</span> <b>{(auditRec.dados?.medicamentos || []).length}</b></p>
              </div>
              {Array.isArray(auditRec.dados?._hist) && auditRec.dados._hist.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 space-y-1">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Edições</p>
                  {auditRec.dados._hist.map((h, i) => (
                    <p key={i} className="text-xs text-slate-600">Editado por <b>{h.por}</b> em {fmtDT(h.em)}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAuditRec(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const PrescricaoCuidados = ({ lista, onAdd, onRemove, readOnly }) => {
  const empty = { cuidado: "", frequencia: "", orientacao: "" };
  const [f, setF] = useState(empty);
  const s = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const salvar = () => { if (!f.cuidado) return; onAdd("prescricao_cuidados", f); setF(empty); };
  return (
    <div className="space-y-4" data-testid="prescricao-cuidados-section">
      {readOnly ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
          Indicações de cuidados definidas pela enfermagem. Siga as orientações abaixo. <span className="text-slate-400">(somente leitura)</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F label="Cuidado"><input className={inputCls} placeholder="ex.: Mudança de decúbito" value={f.cuidado} onChange={(e) => s("cuidado", e.target.value)} /></F>
          <F label="Frequência"><input className={inputCls} placeholder="ex.: 2/2h" value={f.frequencia} onChange={(e) => s("frequencia", e.target.value)} /></F>
          <F label="Orientação"><input className={inputCls} value={f.orientacao} onChange={(e) => s("orientacao", e.target.value)} /></F>
          <div className="sm:col-span-3"><Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar}><Plus className="w-4 h-4" /> Adicionar cuidado</Button></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {lista.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma prescrição de cuidados registrada.</p>}
        {lista.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><HandHeart className="w-4 h-4 text-emerald-600" /><p className="font-semibold text-slate-800">{r.dados.cuidado}</p></div>
              {!readOnly && <button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
            </div>
            {r.dados.frequencia && <p className="text-sm text-slate-600 mt-1">Frequência: {r.dados.frequencia}</p>}
            {r.dados.orientacao && <p className="text-xs text-slate-500 mt-0.5">{r.dados.orientacao}</p>}
            <p className="text-[11px] text-slate-400 mt-2">{fmtDT(r.data)} · {r.autor}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Anotação de Enfermagem ----
const AnotacaoEnfermagem = ({ lista, onAdd, onRemove, onUpdate, rid, podeApagar, podeEditarSempre }) => {
  const { gerarAnotacaoInteligente } = useApp();
  const { toast } = useToast();
  const [texto, setTexto] = useState("");
  const [dataHora, setDataHora] = useState(agoraISO());
  const [gerando, setGerando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTexto, setEditTexto] = useState("");
  const hojeStr = new Date().toISOString().slice(0, 10);
  const salvar = () => { if (!texto.trim()) return; onAdd("anotacao_enfermagem", { texto, _data: dataHora || agoraISO() }); setTexto(""); setDataHora(agoraISO()); };
  const gerarIA = async () => {
    setGerando(true);
    try {
      const res = await gerarAnotacaoInteligente(rid);
      setTexto(res.texto || "");
      toast({ title: `Rascunho do plantão ${res.turno || ""}`, description: "Revise e ajuste antes de registrar." });
    } catch (e) {
      toast({ title: "Não foi possível montar", description: e?.response?.data?.detail || "Sem cuidados no plantão atual." });
    } finally { setGerando(false); }
  };
  const podeEditar = (r) => podeEditarSempre || (r.data || "").startsWith(hojeStr);
  const iniciarEdicao = (r) => { setEditId(r.id); setEditTexto(r.dados.texto || ""); };
  const salvarEdicao = async () => { await onUpdate(editId, { texto: editTexto }); setEditId(null); };
  const ordenada = [...lista].sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-slate-600">Anotação de enfermagem (cronológica — qualquer horário)</p>
          <Button variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={gerarIA} disabled={gerando} data-testid="anotacao-ia-btn">
            <ListChecks className="w-4 h-4" /> {gerando ? "Montando..." : "Montar da linha do tempo"}
          </Button>
        </div>
        <F label="Data e hora"><input type="datetime-local" className={inputCls} value={dataHora} onChange={(e) => setDataHora(e.target.value)} data-testid="anotacao-data" /></F>
        <F label="Anotação de enfermagem"><textarea rows={5} className={inputCls} value={texto} onChange={(e) => setTexto(e.target.value)} data-testid="anotacao-texto" placeholder="Aceitação de dieta, higiene, eliminações, humor, intercorrências do plantão..." /></F>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar} data-testid="anotacao-registrar"><Plus className="w-4 h-4" /> Registrar anotação</Button>
      </div>
      <div className="space-y-3">
        {ordenada.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma anotação de enfermagem.</p>}
        {ordenada.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid={`anotacao-item-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{fmtDT(r.data)} · {r.autor}</span>
              <div className="flex items-center gap-3">
                {editId !== r.id && podeEditar(r) && (
                  <button onClick={() => iniciarEdicao(r)} className="text-slate-300 hover:text-blue-500" title="Editar" data-testid={`anotacao-editar-${r.id}`}><Pencil className="w-4 h-4" /></button>
                )}
                {podeApagar && (
                  <button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500" title="Apagar" data-testid={`anotacao-apagar-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            {editId === r.id ? (
              <div className="mt-2 space-y-2">
                <textarea rows={5} className={inputCls} value={editTexto} onChange={(e) => setEditTexto(e.target.value)} data-testid="anotacao-edit-texto" />
                <div className="flex gap-2">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={salvarEdicao} data-testid="anotacao-edit-salvar"><Plus className="w-4 h-4" /> Salvar edição</Button>
                  <Button variant="outline" onClick={() => setEditId(null)}>Cancelar</Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{r.dados.texto}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Balanço Hídrico ----
const BalancoHidrico = ({ lista, onAdd, onRemove }) => {
  const empty = { via: "Ingesta", tipo: "", volume: "" };
  const [f, setF] = useState(empty);
  const s = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const salvar = () => { if (!f.volume) return; onAdd("balanco_hidrico", f); setF(empty); };
  const totais = lista.reduce((acc, r) => {
    const v = Number(r.dados.volume) || 0;
    if (r.dados.via === "Ingesta") acc.ingesta += v; else acc.eliminacao += v;
    return acc;
  }, { ingesta: 0, eliminacao: 0 });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4"><p className="text-xs text-slate-400">Ingesta total</p><p className="text-lg font-bold text-blue-600">{totais.ingesta} mL</p></div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4"><p className="text-xs text-slate-400">Eliminação total</p><p className="text-lg font-bold text-amber-600">{totais.eliminacao} mL</p></div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4"><p className="text-xs text-slate-400">Balanço</p><p className={`text-lg font-bold ${totais.ingesta - totais.eliminacao >= 0 ? "text-emerald-600" : "text-red-600"}`}>{totais.ingesta - totais.eliminacao} mL</p></div>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <F label="Tipo de registro">
          <Select value={f.via} onValueChange={(v) => s("via", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["Ingesta", "Eliminação"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Descrição"><input className={inputCls} placeholder="ex.: Água, Soro, Diurese" value={f.tipo} onChange={(e) => s("tipo", e.target.value)} /></F>
        <F label="Volume (mL)"><input type="number" className={inputCls} value={f.volume} onChange={(e) => s("volume", e.target.value)} /></F>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={salvar}><Plus className="w-4 h-4" /> Registrar</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-400 border-b border-slate-100">
            {["Data/Hora", "Tipo", "Descrição", "Volume", "Autor", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {lista.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">Nenhum registro.</td></tr>}
            {lista.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-2.5 text-slate-500">{fmtDT(r.data)}</td>
                <td className="px-4 py-2.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${r.dados.via === "Ingesta" ? "text-blue-700 bg-blue-50" : "text-amber-700 bg-amber-50"}`}>{r.dados.via}</span></td>
                <td className="px-4 py-2.5 text-slate-600">{r.dados.tipo}</td>
                <td className="px-4 py-2.5 font-medium text-slate-700">{r.dados.volume} mL</td>
                <td className="px-4 py-2.5 text-slate-500">{r.autor}</td>
                <td className="px-4 py-2.5"><button onClick={() => onRemove(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- Linha do Tempo (passagem de plantão) ----
const TIPO_META = {
  sinais_vitais: { label: "Sinais Vitais", cls: "text-teal-700 bg-teal-50" },
  medicacao: { label: "Medicação", cls: "text-indigo-700 bg-indigo-50" },
  evolucao: { label: "Evolução", cls: "text-blue-700 bg-blue-50" },
  ocorrencia: { label: "Ocorrência", cls: "text-amber-700 bg-amber-50" },
  anotacao_enfermagem: { label: "Anot. Enfermagem", cls: "text-violet-700 bg-violet-50" },
  prescricao_cuidados: { label: "Prescrição de Cuidados", cls: "text-emerald-700 bg-emerald-50" },
  balanco_hidrico: { label: "Balanço Hídrico", cls: "text-sky-700 bg-sky-50" },
  evacuacao: { label: "Evacuação", cls: "text-orange-700 bg-orange-50" },
  diurese: { label: "Diurese", cls: "text-yellow-700 bg-yellow-50" },
  alimentacao: { label: "Alimentação", cls: "text-lime-700 bg-lime-50" },
  banho: { label: "Banho", cls: "text-cyan-700 bg-cyan-50" },
  medicacao_adm: { label: "Adm. Medicação", cls: "text-indigo-700 bg-indigo-50" },
};

const resumoRegistro = (r) => {
  const d = r.dados || {};
  switch (r.tipo) {
    case "sinais_vitais":
      return [d.pas && d.pad && `PA ${d.pas}x${d.pad}`, d.fc && `FC ${d.fc}`, d.temp && `Temp ${d.temp}°`,
        d.spo2 && `SpO2 ${d.spo2}%`, d.glicemia && `Gli ${d.glicemia}`].filter(Boolean).join(" · ");
    case "medicacao":
      return [d.medicamento, d.dose, d.horarios && `Horários: ${d.horarios}`].filter(Boolean).join(" · ");
    case "balanco_hidrico":
      return `${d.via || ""}${d.tipo ? " - " + d.tipo : ""}${d.volume ? " · " + d.volume + " mL" : ""}`.trim();
    case "anotacao_enfermagem":
      return `${d.turno ? "Turno " + d.turno + ": " : ""}${d.texto || ""}`;
    case "prescricao_cuidados":
      return [d.cuidado, d.frequencia && `(${d.frequencia})`, d.orientacao].filter(Boolean).join(" · ");
    case "ocorrencia":
      return `${d.gravidade ? "[" + d.gravidade + "] " : ""}${d.texto || ""}`;
    case "evacuacao":
      return [d.quantidade, d.aspecto, d.obs].filter(Boolean).join(" · ");
    case "diurese":
      return [d.desprezada === false ? "Diurese registrada" : "Diurese desprezada", d.obs].filter(Boolean).join(" · ");
    case "alimentacao":
      return [d.refeicao, d.aceitacao && `aceitação ${d.aceitacao.toLowerCase()}`, d.tipo, d.obs].filter(Boolean).join(" · ");
    case "banho":
      return [d.tipo, d.obs].filter(Boolean).join(" · ");
    case "medicacao_adm":
      return `Horário ${d.horario || ""}: ${d.status === "administrada" ? "Administrada" : "Não administrada"}${d.motivo ? " — " + d.motivo : ""}`;
    case "evolucao":
      return `${d.categoria ? d.categoria + ": " : ""}${d.texto || ""}`;
    default:
      return "";
  }
};

const LinhaTempo = ({ registros }) => {
  const ordenados = [...registros]
    .filter((r) => r.tipo !== "anotacao_enfermagem" && r.tipo !== "receituario")
    .sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="linha-tempo">
      <p className="text-sm text-slate-500 mb-4">Cuidados registrados em ordem cronológica — base para a passagem de plantão. (A anotação de enfermagem completa fica na aba própria.)</p>
      {ordenados.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhum registro no prontuário ainda.</p>}
      <div className="relative pl-5 border-l-2 border-slate-100 space-y-5">
        {ordenados.map((r) => {
          const meta = TIPO_META[r.tipo] || { label: r.tipo, cls: "text-slate-700 bg-slate-50" };
          return (
            <div key={r.id} className="relative">
              <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-blue-500 ring-2 ring-white" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
                <span className="text-[11px] text-slate-400">{fmtDT(r.data)}</span>
                {r.autor && <span className="text-[11px] text-slate-400">· {r.autor}</span>}
              </div>
              <p className="text-sm text-slate-700 mt-1">{resumoRegistro(r) || "—"}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Prontuario;